import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton } from "@/components/ios";
import { SignaturePad } from "@/components/SignaturePad";
import { KeyRound, Loader2, CheckCircle2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function HandoverPortal() {
  const { token } = useParams();
  const [h, setH] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => axios.get(`${API}/handover-portal/${token}`).then(({ data }) => { setH(data); setName(data.to_party?.name || ""); })
    .catch(() => setErr("This link is invalid or has expired."));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const sign = async () => {
    if (!name.trim() || !sig) return toast.error("Enter your name and sign");
    setBusy(true);
    try {
      const gps = await new Promise((res) => navigator.geolocation
        ? navigator.geolocation.getCurrentPosition((p) => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null))
        : res(null));
      await axios.post(`${API}/handover-portal/${token}/sign`, { name: name.trim(), role: h.to_party?.role, signature_image: sig, gps });
      toast.success("Thank you — receipt signed");
      load();
    } catch (e) { toast.error("Could not sign"); }
    finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center text-secondary" style={{ background: "var(--bg)" }}>{err}</div>;
  if (!h) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg)" }}><Loader2 className="animate-spin accent-text" /></div>;

  const signed = h.status === "completed";

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <header className="glass sticky top-0 z-20 border-b px-5 py-4 flex items-center gap-3" style={{ borderColor: "var(--border)" }}>
        <div className="h-9 w-9 rounded-xl grid place-items-center text-white" style={{ background: "var(--accent)" }}><KeyRound size={18} /></div>
        <div><div className="font-semibold" style={{ color: "var(--text)" }}>Key Handover Receipt</div><div className="text-xs text-secondary">{h.property?.address}, {h.property?.suburb}</div></div>
      </header>

      <main className="max-w-lg mx-auto px-5 py-6">
        <Surface className="p-5">
          <div className="text-sm text-secondary">{h.direction === "return_in" ? "Returning keys" : "Receiving keys"}</div>
          <h1 className="text-xl font-bold tracking-tight mt-0.5" style={{ color: "var(--text)" }}>{h.to_party?.name}</h1>
          <div className="text-sm mt-2" style={{ color: "var(--text)" }}>{(h.keys || []).map((k) => `${k.label} ×${k.count}`).join(", ")}</div>
          <div className="text-sm text-secondary mt-0.5">{h.total_sets} key(s)/set(s) total</div>
          {h.description && <div className="text-sm text-secondary mt-1">{h.description}</div>}
        </Surface>

        {(h.media || []).length > 0 && (
          <div className="grid grid-cols-3 gap-2 mt-4">
            {h.media.map((m) => <img key={m.id} src={mediaUrl(m.signed_url)} alt="keys" className="w-full aspect-square object-cover rounded-lg" />)}
          </div>
        )}

        {signed ? (
          <Surface className="p-6 mt-4 text-center">
            <CheckCircle2 size={40} className="accent-text mx-auto" />
            <div className="font-semibold mt-2" style={{ color: "var(--text)" }}>Receipt signed</div>
            <div className="text-sm text-secondary">Signed by {h.signatures?.[0]?.signer} on {h.signatures?.[0] && new Date(h.signatures[0].at).toLocaleString("en-AU")}</div>
          </Surface>
        ) : (
          <Surface className="p-5 mt-4">
            <div className="font-medium mb-2" style={{ color: "var(--text)" }}>Confirm you have received these keys</div>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" data-testid="hp-name"
              className="w-full text-sm rounded-lg px-3 py-2 outline-none border mb-2" style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }} />
            <SignaturePad testid="hp-signature" onChange={setSig} />
            <AButton testid="hp-sign-btn" className="w-full mt-3" onClick={sign} disabled={busy}>{busy ? "Saving…" : "I confirm & sign"}</AButton>
          </Surface>
        )}
        <p className="text-xs text-secondary text-center mt-6 flex items-center justify-center gap-1"><MapPin size={11} /> Your signature, name, time and location are recorded as proof of handover.</p>
      </main>
    </div>
  );
}
